import React, { useState } from "react";
import {
    Box,
    Typography,
    Chip,
    Tooltip,
    Stack,
    IconButton,
    Collapse,
    Card,
    CardContent,
    Divider,
    Grid,
    useTheme,
    useMediaQuery
} from "@mui/material";
import {
    ExpandMore,
    Phone,
    Email,
    AccountBalance,
    Payment,
    PendingActions,
    Person,
    CalendarToday,
    LocalOffer
} from "@mui/icons-material";
import PaymentsList from "../PaymentsList";
import { useNavigate } from "react-router-dom";

const money = (n) =>
    (n ?? 0).toLocaleString(undefined, { style: "currency", currency: "LKR", maximumFractionDigits: 0 });

export default function PendingRow({ row }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Card
            sx={{
                width: '100%',
                mb: 2,
                borderRadius: 2,
                boxShadow: theme.shadows[2],
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                    boxShadow: theme.shadows[6],
                    transform: 'translateY(-2px)'
                },
                maxWidth: '100%',
                boxSizing: 'border-box'
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Header Section - Student Info */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box flex={1}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                            <Person sx={{ fontSize: 30, color: '#777777ff' }} />
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                fontWeight="bold"
                                sx={{
                                    color: 'text.primary',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {row.student.name}
                            </Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                            <Tooltip title={`Main Profile`}>
                                <Chip
                                    onClick={() => { navigate('/student-details/'+row.student.id) }}
                                    size="medium"
                                    label={`ID: ${row.student.id}`}
                                    variant="outlined"
                                    sx={{ fontWeight: 'medium', color: '#130894ff', borderColor: '#130894ff' }}
                                />
                            </Tooltip>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Registered: {row.register_date}
                            </Typography>
                        </Stack>
                    </Box>

                    <Stack direction="column" alignItems="flex-end" spacing={1}>
                        {/* Expand/Collapse Button */}
                        <IconButton
                            size="small"
                            onClick={() => setOpen((v) => !v)}
                            sx={{
                                bgcolor: '#828282ff',
                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "all 0.3s ease-in-out",
                                '&:hover': {
                                    bgcolor: '#484848ff',
                                    transform: open ? "rotate(180deg) scale(1.1)" : "rotate(0deg) scale(1.1)"
                                }
                            }}
                        >
                            <ExpandMore sx={{ color: '#FFFFFF', }} />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Contact Actions */}
                <Stack direction="row" spacing={1} mb={3}>
                    <Tooltip title={`Call ${row.student.phone}`}>
                        <IconButton
                            component="a"
                            href={`tel:${row.student.phone}`}
                            sx={{
                                bgcolor: 'success.main',
                                '&:hover': {
                                    bgcolor: 'success.dark',
                                    transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <Phone sx={{ color: '#FFFFFF', }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={`Email ${row.student.email}`}>
                        <IconButton
                            component="a"
                            href={`mailto:${row.student.email}`}
                            sx={{
                                bgcolor: 'info.main',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'info.dark',
                                    transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <Email sx={{ color: '#FFFFFF', }} />
                        </IconButton>
                    </Tooltip>
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* Financial Information Grid */}
                <Grid container spacing={2} mb={2}>
                    <Grid item xs={6} sm={6} md={3} lg={2}>
                        <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.50', height: '100%' }}>
                            <Stack alignItems="center" spacing={1} sx={{ height: '100%', justifyContent: 'center' }}>
                                <AccountBalance sx={{ color: '#B027F5', fontSize: { xs: 20, sm: 24 } }} />
                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                    Course Amount
                                </Typography>
                                <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold" color="#B027F5">
                                    {money(row.amount)}
                                </Typography>
                            </Stack>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={6} md={3} lg={2}>
                        <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.50', height: '100%' }}>
                            <Stack alignItems="center" spacing={1} sx={{ height: '100%', justifyContent: 'center' }}>
                                <LocalOffer color="success" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                    Discount
                                </Typography>
                                <Chip
                                    label={`${row.discount}%`}
                                    color="success"
                                    size="small"
                                    sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                                />
                            </Stack>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={6} md={3} lg={2}>
                        <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.50', height: '100%' }}>
                            <Stack alignItems="center" spacing={1} sx={{ height: '100%', justifyContent: 'center' }}>
                                <Payment color="info" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                    Expected Amount
                                </Typography>
                                <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold" color="info.main">
                                    {money(row.expected)}
                                </Typography>
                            </Stack>
                        </Card>
                    </Grid>

                    <Grid item xs={6} sm={6} md={3} lg={2}>
                        <Card variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.50', height: '100%' }}>
                            <Stack alignItems="center" spacing={1} sx={{ height: '100%', justifyContent: 'center' }}>
                                <AccountBalance color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                    Total Paid
                                </Typography>
                                <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold" color="primary.main">
                                    {money(row.total_paid)}
                                </Typography>
                            </Stack>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} lg={4}>
                        <Card
                            variant="outlined"
                            sx={{
                                p: 2,
                                textAlign: 'center',
                                bgcolor: row.pending === 0 ? 'secondary.50' : 'warning.50',
                                border: `2px solid ${row.pending === 0 ? theme.palette.secondary.main : theme.palette.warning.main}`,
                                height: '100%'
                            }}
                        >
                            <Stack alignItems="center" spacing={1} sx={{ height: '100%', justifyContent: 'center' }}>
                                <PendingActions
                                    sx={{
                                        fontSize: { xs: 24, sm: 28 },
                                        color: row.pending === 0 ? 'secondary.main' : 'warning.main'
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                >
                                    Pending Amount
                                </Typography>
                                <Typography
                                    variant={isMobile ? "h6" : "h5"}
                                    fontWeight="bold"
                                    color={row.pending === 0 ? "secondary.main" : "warning.main"}
                                >
                                    {money(row.pending)}
                                </Typography>
                                {row.pending === 0 && (
                                    <Chip
                                        label="PAID IN FULL"
                                        color="secondary"
                                        size="small"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                )}
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>

                {/* Collapsible Payment History */}
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box mt={3}>
                        <Divider sx={{ my: 2 }} />
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <Payment color="success" />
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                                Payment History
                            </Typography>
                        </Stack>
                        <Box>
                            <PaymentsList payments={row.payments} />
                        </Box>
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
}